/**
 * Defensive patch for the well-known React reconciliation crash:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * Root cause is virtually always a browser extension (Google Translate,
 * Grammarly, LastPass, ad blockers) that wraps or re-parents DOM nodes that
 * React thinks it still owns. When React's commit phase later tries to
 * remove or move those nodes, the cached parent reference doesn't match the
 * live DOM, and the native `removeChild` / `insertBefore` calls throw.
 *
 * The crash typically surfaces when a portal-using component (Radix Dialog,
 * Select, Popover, etc.) unmounts — e.g. pressing Escape on a modal — which
 * is exactly what the QA report saw on /calendar.
 *
 * We patch the two Node methods to fall back to the safe behavior (no-op for
 * removeChild, append for insertBefore) instead of throwing. This is the
 * widely-used recovery for the Google-Translate-vs-React class of bugs and
 * has been blessed in React's own GitHub issues.
 */

let installed = false;

export function installDomNodeRemovalGuard(): void {
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;

  type RemoveChildFn = <T extends Node>(this: Node, child: T) => T;
  type InsertBeforeFn = <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ) => T;

  const proto = Node.prototype as Node & {
    removeChild: RemoveChildFn;
    insertBefore: InsertBeforeFn;
  };

  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;

  proto.removeChild = function patchedRemoveChild<T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      // The node we were asked to remove is no longer ours. Something else
      // (extension, transition library, native popover) already moved it.
      // Returning the child without throwing matches the contract React relies
      // on and lets the commit phase finish without aborting the whole tree.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function patchedInsertBefore<T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was relocated. Fall back to appending the new node
      // instead of throwing — visually equivalent in nearly all cases and
      // avoids tearing down the entire route segment.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
